import { ConfirmationDialog } from "@biom3/react";
import Image from "next/image";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";

import { MODAL_HEIGHT, MODAL_WIDTH } from "@/constants";
import { useUnlinkWallet } from "@/hooks";
import type { Wallet } from "@/types";
import { trackEvent } from "@analytics";

type UnlinkWalletModalProps = {
  wallet?: Wallet | null;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
};

export const UnlinkWalletModalComponent = ({
  error,
  wallet,
  isOpen,
  isPending,
  onUnlink,
  onClose,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error?: any;
  isOpen: boolean;
  wallet: Wallet;
  isPending: boolean;
  onUnlink: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  if (error) {
    return (
      <ConfirmationDialog
        visible={true}
        outsideClicksClose
        onCloseModal={onClose}
      >
        <ConfirmationDialog.Content
          testId="UnlinkWalletErrorModal"
          sx={{
            height: MODAL_HEIGHT,
            width: MODAL_WIDTH,
          }}
        >
          <ConfirmationDialog.Content.CloseButtCon onClick={onClose} />
          <ConfirmationDialog.Content.Image
            alt={t("unlink_wallet_failed_modal_title")}
            use={
              <Image
                src="/images/wallets/unlink.svg"
                width={430}
                height={240}
                alt={t("unlink_wallet_failed_modal_title")}
              />
            }
          />
          <ConfirmationDialog.Content.Title testId="UnlinkWalletErrorModal-Title">
            {t("unlink_wallet_failed_modal_title")}{" "}
          </ConfirmationDialog.Content.Title>
          <ConfirmationDialog.Content.Caption testId="UnlinkWalletErrorModal-Caption">
            {t("unlink_wallet_failed_modal_caption")}{" "}
          </ConfirmationDialog.Content.Caption>
          <ConfirmationDialog.Content.Button
            testId="UnlinkWalletErrorModal-CloseModalButton"
            size="large"
            variant="tertiary"
            onClick={onClose}
          >
            {t("close")}
          </ConfirmationDialog.Content.Button>
        </ConfirmationDialog.Content>
      </ConfirmationDialog>
    );
  }

  return (
    <ConfirmationDialog
      visible={isOpen}
      outsideClicksClose
      onCloseModal={onClose}
    >
      <ConfirmationDialog.Content
        testId="UnlinkWalletModal"
        sx={{
          height: MODAL_HEIGHT,
          width: MODAL_WIDTH,
        }}
      >
        <ConfirmationDialog.Content.Image
          alt={t("unlink_wallet_modal_title")}
          use={
            <Image
              src="/images/wallets/unlink.svg"
              width={430}
              height={240}
              alt={t("unlink_wallet_modal_title")}
            />
          }
        />
        <ConfirmationDialog.Content.Title testId="UnlinkWalletModal-Title">
          {" "}
          {t("unlink_wallet_modal_title")}{" "}
        </ConfirmationDialog.Content.Title>
        <ConfirmationDialog.Content.Caption testId="UnlinkWalletModal-Caption">
          {t("unlink_wallet_modal_caption", { address: wallet?.address })}
        </ConfirmationDialog.Content.Caption>
        <ConfirmationDialog.Content.Button
          disabled={isPending}
          testId="UnlinkWalletModal-ConfirmModalButton"
          size="large"
          variant="primary/fatal"
          onClick={onUnlink}
        >
          {t("unlink_wallet_confirm")}
        </ConfirmationDialog.Content.Button>
        <ConfirmationDialog.Content.Button
          testId="UnlinkWalletModal-CloseModalButton"
          size="large"
          variant="tertiary"
          onClick={onClose}
        >
          {t("cancel")}
        </ConfirmationDialog.Content.Button>
      </ConfirmationDialog.Content>
    </ConfirmationDialog>
  );
};

export const UnlinkWalletModal = ({
  wallet,
  isModalOpen,
  setIsModalOpen,
}: UnlinkWalletModalProps) => {
  const {
    mutate: unlink,
    isPending,
    isSuccess,
    error,
    reset,
  } = useUnlinkWallet();

  const onUnlink = () => {
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "Confirm",
        section: "UnlinkWalletModal",
      },
    });
    setIsModalOpen(false);
    reset();
    unlink(wallet?.address);
  };

  const onClose = useCallback(async () => {
    setIsModalOpen(false);
    reset(); // Reset the error state when closing
  }, [setIsModalOpen, reset]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  if (!wallet) {
    return <></>;
  }

  return (
    <UnlinkWalletModalComponent
      error={error}
      wallet={wallet}
      isOpen={isModalOpen}
      isPending={isPending}
      onClose={() => {
        trackEvent({
          component: "Button",
          action: "Pressed",
          extras: {
            control: "Close",
            section: "UnlinkWalletModal",
          },
        });
        onClose();
      }}
      onUnlink={onUnlink}
    />
  );
};
